/**
 * API response types for parties endpoints.
 * Used by mobile and web API route responses. Dates are ISO strings in JSON.
 */

import type { PartyJoinAttribution } from "../party-join-attribution";
import type { PartyPassCheckoutSource } from "../party-pass-checkout";
import type { NativePartyPassCheckoutContext } from "../native-checkout";
import type { TieBreakerMode, TieBreakerSummary } from "../tie-breakers";

export type { TieBreakerMode, TieBreakerSummary };

export type ParticipantRole = "OWNER" | "MEMBER";

export type PartyPlan = "free" | "party_pass";

export type PartyUnlockSource =
  | "stripe"
  | "apple_iap"
  | "google_play"
  | "admin"
  | "split_prompt";

export type PartyFeature =
  | "branding"
  | "chat"
  | "expandedLeaderboard"
  | "recap"
  | "liveReactions";

export interface ApiUser {
  id: string;
  clerkId: string;
  email: string;
  name: string | null;
  username: string | null;
  imageUrl: string | null;
  createdAt: string;
}

export interface ApiAwardShow {
  id: string;
  name: string;
  year: number;
  startTime: string | null;
  timezone: string | null;
  status: string;
  externalRef: string | null;
  redCarpetUrl: string | null;
  imageUrl: string | null;
  createdAt: string;
}

export interface ApiCategory {
  id: string;
  awardShowId: string;
  name: string;
  fieldName: string | null;
  orderIndex: number;
  createdAt: string;
  nominees?: ApiNominee[];
}

export interface ApiNominee {
  id: string;
  categoryId: string;
  name: string;
  additionalInfo: string | null;
  imageUrl: string | null;
  externalRef: string | null;
  createdAt: string;
}

/** GET /api/award-shows/published — item with categories and nominees (dates as ISO strings) */
export interface PublishedAwardShow extends ApiAwardShow {
  categories: Array<ApiCategory & { nominees: ApiNominee[] }>;
}

/** GET /api/award-shows/published — response body */
export type GetPublishedAwardShowsResponse = PublishedAwardShow[];

/** POST /api/parties — request body */
export interface CreatePartyBody {
  name: string;
  awardShowId: string;
  /** Ignored. New parties follow the award show start. */
  votingClosesAt?: string | null;
  timezone?: string;
}

/** POST /api/parties and POST /api/parties/join — response body (serialized party) */
export interface CreatePartyResponse {
  id: string;
  name: string;
  awardShowId: string;
  ownerId: string;
  inviteCode: string;
  /** Effective deadline. Null when the show has no start and no override. */
  votingClosesAt: string | null;
  timezone: string;
  isManuallyLocked: boolean;
  hideMemberEmails: boolean;
  plan: PartyPlan;
  memberCapOverride: number | null;
  unlockSource: PartyUnlockSource | null;
  paidAt: string | null;
  createdAt: string;
}

/** POST /api/parties/join — response body */
export type JoinPartyResponse = CreatePartyResponse;

/** POST /api/parties/[partyId]/vote — success response body */
export type SubmitVoteResponse = { success: true };

/** GET /api/parties/[partyId]/votes/me — categoryId -> nomineeId */
export type GetMyVotesResponse = Record<string, string>;

/** POST /api/parties/[partyId]/reactions — success response body */
export type ToggleReactionResponse = { success: true };

/** GET /api/parties — list item for "My Parties" */
export interface MyParty {
  id: string;
  name: string;
  awardShowId: string;
  ownerId: string;
  inviteCode: string;
  votingClosesAt: string | null;
  timezone: string | null;
  isManuallyLocked: boolean;
  plan: PartyPlan;
  memberCapOverride: number | null;
  unlockSource: PartyUnlockSource | null;
  paidAt: string | null;
  createdAt: string;
  role: ParticipantRole;
  participantCount: number;
  totalCategories: number;
  announcedResultsCount: number;
  userVoteCount: number;
  awardShow: ApiAwardShow;
  /** Used to count unique friends on the parties list. */
  participants: Array<{ userId: string }>;
}

/** GET /api/parties — pending invitation item */
export interface PendingInvitation {
  id: string;
  partyId: string;
  clerkInvitationId: string;
  email: string;
  name: string | null;
  userId: string | null;
  status: string;
  invitedAt: string;
  acceptedAt: string | null;
  party: {
    id: string;
    name: string;
    awardShowId: string;
    ownerId: string;
    inviteCode: string;
    votingClosesAt: string | null;
    timezone: string | null;
    isManuallyLocked: boolean;
    plan: PartyPlan;
    memberCapOverride: number | null;
    unlockSource: PartyUnlockSource | null;
    paidAt: string | null;
    createdAt: string;
    awardShow: ApiAwardShow;
    owner: ApiUser;
    participants: unknown[];
  };
}

/** GET /api/parties — response body */
export interface GetPartiesResponse {
  parties: MyParty[];
  pendingInvitations: PendingInvitation[];
}

/** Public join page — display-safe party payload (no email / clerkId). */
export interface PublicUserPreview {
  id: string;
  name: string | null;
  username: string | null;
  imageUrl: string | null;
}

export interface PartyInvitePreview {
  id: string;
  name: string;
  ownerId: string;
  inviteCode: string;
  votingClosesAt: string | null;
  timezone: string | null;
  isManuallyLocked: boolean;
  plan: PartyPlan;
  memberCapOverride: number | null;
  awardShow: {
    id: string;
    name: string;
    year: number;
    startTime: string | null;
    imageUrl: string | null;
  };
  owner: PublicUserPreview;
  participants: Array<{
    id: string;
    userId: string;
    user: PublicUserPreview;
  }>;
  mergedIntoPartyId: string | null;
  mergedIntoInviteCode: string | null;
  /** Prospect preview. The invite page hides join controls when this is set. */
  isSample: boolean;
}

export type PartyMutationSuccessResponse = { success: true };

export type LockPartyResponse = PartyMutationSuccessResponse;
export type UnlockPartyResponse = PartyMutationSuccessResponse;
export type DeletePartyResponse = PartyMutationSuccessResponse;
export type RemoveParticipantResponse = PartyMutationSuccessResponse;
export type RevokeInvitationResponse = PartyMutationSuccessResponse;

export type FriendInviteStatus =
  | "PENDING"
  | "ACCEPTED"
  | "DECLINED"
  | "REVOKED";

export interface FriendSharedParty {
  id: string;
  name: string;
  awardShowName: string;
  awardShowYear: number;
  sharedAt: string;
}

export interface FriendSummary {
  userId: string;
  name: string | null;
  username: string | null;
  email: string;
  imageUrl: string | null;
  sharedPartyCount: number;
  sharedParties: FriendSharedParty[];
  lastSharedAt: string;
}

export interface RemovedFriendSummary extends FriendSummary {
  removedAt: string;
}

export interface FriendHostedParty {
  id: string;
  name: string;
  awardShowName: string;
  participantUserIds: string[];
  pendingInviteeIds: string[];
}

export interface FriendsListResponse {
  friends: FriendSummary[];
  removed: RemovedFriendSummary[];
  hostedParties: FriendHostedParty[];
}

export interface FriendPartyInvite {
  id: string;
  partyId: string;
  status: FriendInviteStatus;
  invitedAt: string;
  inviter: PublicUserPreview;
  invitee: PublicUserPreview;
  party: {
    id: string;
    name: string;
    inviteCode: string;
    awardShow: {
      id: string;
      name: string;
      year: number;
      imageUrl: string | null;
    };
    participantCount: number;
  };
}

export type FriendInvitePage =
  | { state: "pending"; invite: FriendPartyInvite }
  | { state: "accepted"; partyId: string }
  | { state: "unavailable" }
  | { state: "notInvitee" };

export interface PartyFriendInviteSummary {
  id: string;
  invitedAt: string;
  status: FriendInviteStatus;
  invitee: PublicUserPreview;
}

export interface InviteFriendBody {
  userId: string;
}

export interface InviteFriendResponse {
  success: true;
  inviteId: string;
}

export type FriendInviteMutationResponse = PartyMutationSuccessResponse;

export interface AcceptFriendInviteResponse {
  success: true;
  partyId: string;
  attribution: PartyJoinAttribution | null;
}

export interface InviteByEmailBody {
  email: string;
  name: string;
  message: string;
}

export interface PartyInvitationDto {
  id: string;
  partyId: string;
  clerkInvitationId: string;
  email: string;
  name: string | null;
  userId: string | null;
  status: string;
  invitedAt: string;
  acceptedAt: string | null;
}

export type InviteByEmailResponse = PartyInvitationDto;

export interface UpdatePartySettingsBody {
  name?: string;
  /**
   * Custom deadline. Null, or followShowStart, clears it so the party locks
   * when the show starts. Omit both to leave the deadline unchanged.
   */
  votingClosesAt?: string | null;
  followShowStart?: boolean;
  timezone?: string;
  hideMemberEmails?: boolean;
  tieBreakerMode?: TieBreakerMode;
}

export type UpdatePartySettingsResponse = CreatePartyResponse & {
  tieBreakerMode?: TieBreakerMode;
};

export interface RequestBlockedJoinNudgeBody {
  inviteCode: string;
}

export interface RequestBlockedJoinNudgeResponse {
  alreadyCaptured: boolean;
  notified: boolean;
}

export interface StartPartyPassCheckoutRequest {
  source?: PartyPassCheckoutSource;
  nativeCheckoutContext?: NativePartyPassCheckoutContext;
}

export interface StartPartyPassCheckoutResponse {
  url: string;
  /**
   * Stripe Checkout must open in the system browser rather than the native
   * WebView. Omitted for ordinary hosted web checkout.
   */
  openExternally?: true;
}

export type PartyPassStorePurchaseRequest =
  | {
      store: "apple";
      signedTransactionInfo: string;
    }
  | {
      store: "google_play";
      purchaseToken: string;
    };

export interface PartyPassStorePurchaseResponse {
  fulfilled: boolean;
}

export type CorrectPrediction = {
  categoryId: string;
  categoryName: string;
  nomineeId: string;
  nomineeName: string;
  nomineeAdditionalInfo: string | null;
};

export type CorrectPredictionsByUser = Record<string, CorrectPrediction[]>;

/** GET /api/parties/[partyId] — party dashboard payload */
export interface PartyParticipant {
  id: string;
  partyId: string;
  userId: string;
  role: ParticipantRole;
  joinedAt: string;
  user: ApiUser;
}

export interface PartyDetail {
  id: string;
  name: string;
  awardShowId: string;
  ownerId: string;
  inviteCode: string;
  /** Effective deadline. Null when the show has no start and no override. */
  votingClosesAt: string | null;
  /** Stored custom deadline. Null means the party follows the show start. */
  votingDeadlineOverride: string | null;
  timezone: string | null;
  isManuallyLocked: boolean;
  hideMemberEmails: boolean;
  plan: PartyPlan;
  memberCapOverride: number | null;
  unlockSource: PartyUnlockSource | null;
  paidAt: string | null;
  createdAt: string;
  splitFromPartyId: string | null;
  mergedIntoPartyId: string | null;
  isLocked: boolean;
  currentUserId: string;
  userVoteCount: number;
  userVotes: Record<string, string>;
  voteCountByUserId: Record<string, number>;
  winnersByCategory: Record<string, string[]>;
  announcedResultsCount: number;
  tieBreakerMode: TieBreakerMode;
  awardShow: ApiAwardShow & {
    categories: Array<ApiCategory & { nominees: ApiNominee[] }>;
  };
  owner: ApiUser;
  participants: PartyParticipant[];
  invitations: Array<{
    id: string;
    partyId: string;
    clerkInvitationId: string;
    email: string;
    name: string | null;
    userId: string | null;
    status: string;
    invitedAt: string;
    acceptedAt: string | null;
    user: ApiUser | null;
  }>;
}

/** GET /api/parties/[partyId]/leaderboard — leaderboard entry */
export interface LeaderboardEntry {
  rank: number;
  userId: string;
  userName: string | null;
  userImage: string | null;
  correctCount: number;
  totalCategories: number;
  isInvited: boolean;
  tieBroken: boolean;
  tieBreakerMode: TieBreakerMode;
  tieBreakerMetric: number;
  tieBreakerSummary: TieBreakerSummary;
  tieBreakerReason: string | null;
}

/** GET /api/parties/[partyId]/leaderboard — response body */
export interface GetLeaderboardResponse {
  leaderboard: LeaderboardEntry[];
  totalCategories: number;
  currentUserId: string;
  isTruncated: boolean;
}

export type PartyRecapStatus =
  | "pending"
  | "generating"
  | "completed"
  | "failed";

export type PartyRecapImpact =
  | "saved_from_last"
  | "took_first"
  | "jumped_podium"
  | "climbed_rankings";

export interface PartyRecapConsensusCategory {
  categoryId: string;
  categoryName: string;
  winnerName: string;
  consensusPickName: string | null;
  pickedByCount: number;
  totalVotes: number;
  winnerPickedByCount: number;
  correctVoterNames: string[];
  wasConsensusWrong: boolean;
  isTie: boolean;
}

export interface PartyRecapLeaderboardPlayer {
  userId: string;
  userName: string;
  correctCount: number;
  totalCategories: number;
  rank: number;
  accuracy: number;
  bestFieldName: string | null;
  bestFieldCorrectCount: number;
  correctCategoryNames: string[];
  exclusiveHitCategoryNames: string[];
  tagline?: string;
}

export interface PartyRecapSwing {
  userId: string;
  userName: string;
  categoryId: string;
  categoryName: string;
  winnerName: string;
  correctVoterCount: number;
  rankWithCategory: number;
  rankWithoutCategory: number;
  scoreWithCategory: number;
  scoreWithoutCategory: number;
  impact: PartyRecapImpact;
}

export interface PartyRecapInsightPanel {
  title: string;
  body: string;
}

export interface PartyRecapInsightPanels {
  topPerformer: PartyRecapInsightPanel;
  swingPick: PartyRecapInsightPanel;
  chaosMeter: PartyRecapInsightPanel;
  nightsArc: PartyRecapInsightPanel | null;
}

export interface PartyRecapPayload {
  partyId: string;
  partyName: string;
  awardShowName: string;
  awardShowYear: number;
  participantCount: number;
  totalCategories: number;
  resolvedCategoryCount: number;
  generatedAt: string;
  headline: string;
  narrative: string;
  shareDescription: string;
  consensusWrongCount: number;
  consensusHighlights: PartyRecapConsensusCategory[];
  leaderboard: PartyRecapLeaderboardPlayer[];
  topPerformer: PartyRecapLeaderboardPlayer | null;
  bottomPerformer: PartyRecapLeaderboardPlayer | null;
  biggestSwing: PartyRecapSwing | null;
  mostControversialCategory?: PartyRecapControversialCategory | null;
  mostUnanimousMiss?: PartyRecapUnanimousMiss | null;
  biggestUpset?: PartyRecapBiggestUpset | null;
  timeline?: PartyRecapTimeline | null;
  insightPanels: PartyRecapInsightPanels;
}

export interface PartyRecapControversialCategory {
  categoryId: string;
  categoryName: string;
  topNomineeName: string;
  topNomineeVotes: number;
  runnerUpNomineeName: string;
  runnerUpVotes: number;
  totalVotes: number;
}

export interface PartyRecapUnanimousMiss {
  categoryId: string;
  categoryName: string;
  consensusPickName: string;
  winnerName: string;
  unanimousVoteCount: number;
  totalVotes: number;
}

export interface PartyRecapBiggestUpset {
  categoryId: string;
  categoryName: string;
  winnerName: string;
  winnerVoteCount: number;
  totalVotes: number;
}

export interface PartyRecapTimeline {
  leadChanges: number;
  longestLeader: {
    userId: string;
    userName: string;
    categoriesInLead: number;
  } | null;
  biggestComeback: {
    userId: string;
    userName: string;
    worstRank: number;
    finalRank: number;
    worstAfterCategory: string;
  } | null;
  crowningCategory: {
    categoryName: string;
    winnerName: string;
    userId: string;
    userName: string;
  } | null;
}

export interface PartyRecapInsightsPayload {
  partyName: string;
  awardShowName: string;
  awardShowYear: number;
  participantCount: number;
  totalCategories: number;
  resolvedCategoryCount: number;
  consensusWrongCount: number;
  consensusHighlights: PartyRecapConsensusCategory[];
  leaderboard: PartyRecapLeaderboardPlayer[];
  topPerformer: PartyRecapLeaderboardPlayer | null;
  bottomPerformer: PartyRecapLeaderboardPlayer | null;
  biggestSwing: PartyRecapSwing | null;
  mostControversialCategory: PartyRecapControversialCategory | null;
  mostUnanimousMiss: PartyRecapUnanimousMiss | null;
  biggestUpset: PartyRecapBiggestUpset | null;
  timeline: PartyRecapTimeline | null;
}

export interface PersonalRecapHighlight {
  partyId: string;
  userId: string;
  highlight: string;
  recapSourceHash: string;
}

export interface GetPartyRecapResponse {
  status: PartyRecapStatus | "not_ready" | "upgrade_required";
  recap: PartyRecapPayload | null;
  publicShareUrl: string | null;
  error: string | null;
  retryCount?: number;
}

/** GET /api/parties/[partyId]/recap/status — response body */
export interface GetPartyRecapStatusResponse {
  status: PartyRecapStatus | "not_ready" | "upgrade_required";
  retryCount: number;
}

// ============ LIVE CATEGORY REACTIONS ============

export const REACTION_EMOJIS = [
  "🎉",
  "😱",
  "😂",
  "🔥",
  "💀",
  "👏",
  "😤",
  "🤷",
] as const;

export type ReactionEmoji = (typeof REACTION_EMOJIS)[number];

export const REACTION_LABELS: Record<ReactionEmoji, string> = {
  "🎉": "Called it!",
  "😱": "Robbed!",
  "😂": "No way!",
  "🔥": "Deserved!",
  "💀": "I'm dead",
  "👏": "Well deserved",
  "😤": "I'm heated",
  "🤷": "Who?",
};

export const LIVE_RESULT_LABEL_DURATION_MS = 2400;

export function removeOptimisticReactor(
  reactors: Partial<Record<ReactionEmoji, string[]>>,
  emoji: ReactionEmoji,
): void {
  const existing = reactors[emoji] ?? [];
  if (existing.length === 0) return;

  const youIndex = existing.indexOf("You");
  if (youIndex >= 0) {
    reactors[emoji] = existing.filter((_, idx) => idx !== youIndex);
    return;
  }

  // Server refreshes contain real display names, not the optimistic "You" marker.
  // In that case, remove one entry so names stay aligned with optimistic counts.
  reactors[emoji] = existing.slice(0, -1);
}

export function applyReactionToggle(
  category: LiveResultCategory,
  emoji: ReactionEmoji,
): LiveResultCategory {
  const prev = category.reactions;
  const counts = { ...prev.counts };
  const reactors = { ...prev.reactors };

  if (prev.userEmoji === emoji) {
    counts[emoji] = Math.max((counts[emoji] ?? 1) - 1, 0);
    if (counts[emoji] === 0) {
      delete counts[emoji];
      delete reactors[emoji];
    } else {
      removeOptimisticReactor(reactors, emoji);
    }
    return {
      ...category,
      reactions: { ...prev, counts, reactors, userEmoji: null },
    };
  }

  if (prev.userEmoji) {
    const old = prev.userEmoji;
    counts[old] = Math.max((counts[old] ?? 1) - 1, 0);
    if (counts[old] === 0) {
      delete counts[old];
      delete reactors[old];
    } else {
      removeOptimisticReactor(reactors, old);
    }
  }

  counts[emoji] = (counts[emoji] ?? 0) + 1;
  reactors[emoji] = [...(reactors[emoji] ?? []), "You"];
  return {
    ...category,
    reactions: { ...prev, counts, reactors, userEmoji: emoji },
  };
}

export const CORRECT_PREDICTION_LABELS = [
  "Boom! Nailed it! 🎯",
  "Called it like a pro! 🧠",
  "Psychic mode: ON 🔮",
  "That pick was fire! 🔥",
  "You saw it coming! 👏",
] as const;

export const WRONG_PREDICTION_LABELS = [
  "Oof, swing and a miss!",
  "Plot twist got you! 🎬",
  "Close, but no trophy! 🏆",
  "The chaos pick lost this round 😅",
  "Bold guess... wrong winner! 🤭",
] as const;

export function getDefaultPredictionLabel(isCorrect: boolean): string {
  return isCorrect
    ? (CORRECT_PREDICTION_LABELS[0] ?? "Nailed it!")
    : (WRONG_PREDICTION_LABELS[0] ?? "Missed this one!");
}

export function getRandomPredictionLabel(
  isCorrect: boolean,
  previous: string | null,
): string {
  const options: readonly string[] = isCorrect
    ? CORRECT_PREDICTION_LABELS
    : WRONG_PREDICTION_LABELS;
  if (options.length <= 1) {
    return options[0] ?? (isCorrect ? "Nailed it!" : "Missed this one!");
  }

  let next =
    options[Math.floor(Math.random() * options.length)] ??
    options[0] ??
    (isCorrect ? "Nailed it!" : "Missed this one!");
  if (previous) {
    let attempts = 0;
    while (next === previous && attempts < 10) {
      next =
        options[Math.floor(Math.random() * options.length)] ??
        options[0] ??
        (isCorrect ? "Nailed it!" : "Missed this one!");
      attempts += 1;
    }
  }

  return next;
}

export interface CategoryReactionSummary {
  categoryId: string;
  counts: Partial<Record<ReactionEmoji, number>>;
  reactors: Partial<Record<ReactionEmoji, string[]>>;
  userEmoji: ReactionEmoji | null;
}

export interface LiveResultCategory {
  categoryId: string;
  categoryName: string;
  winnerName: string;
  winnerAdditionalInfo: string | null;
  winnerImageUrl: string | null;
  userPredictedCorrectly: boolean;
  announcedAt: string;
  reactions: CategoryReactionSummary;
}

/** GET /api/parties/[partyId]/live-results — response body */
export interface GetLiveResultsResponse {
  categories: LiveResultCategory[];
}

// ============ PARTY CHAT ============

export interface PartyChatSender {
  id: string;
  name: string;
  imageUrl: string | null;
}

export type PartyChatMessageKind = "user" | "winner" | "podium";

export type PodiumPassVariant =
  | "claim"
  | "takes"
  | "movesPast"
  | "jumps"
  | "knocks"
  | "stepsOn"
  | "catches"
  | "pullsEven"
  | "joins"
  | "beside";

export type PodiumPassStyle = "pass" | "catch";

export type PodiumPlace = 1 | 2 | 3;

/** A podium change derived from announced results. Not stored as a chat row. */
export interface PodiumPass {
  categoryId: string;
  announcedAt: string;
  climberUserId: string;
  climberName: string;
  passedUserId: string;
  passedName: string;
  fromPlace: number;
  toPlace: PodiumPlace;
  style: PodiumPassStyle;
  variant: PodiumPassVariant;
  /** Set when more than one member moved into `toPlace` on the same call. */
  otherClimbers?: PodiumOtherClimbers;
}

export interface PodiumOtherClimbers {
  /** Up to two names besides `climberName`, in display order. */
  names: string[];
  /** Everyone besides `climberName` who moved into the place. */
  count: number;
}

export interface PartyChatUserMessage {
  kind: "user";
  id: string;
  partyId: string;
  userId: string;
  body: string;
  createdAt: string;
  sender: PartyChatSender;
}

export interface PartyChatWinnerMessage {
  kind: "winner";
  id: string;
  partyId: string;
  createdAt: string;
  winner: LiveResultCategory;
}

export interface PartyChatPodiumMessage {
  kind: "podium";
  id: string;
  partyId: string;
  createdAt: string;
  pass: PodiumPass;
}

export type PartyChatMessage =
  | PartyChatUserMessage
  | PartyChatWinnerMessage
  | PartyChatPodiumMessage;

export type PartyChatAccess = "allowed" | "upgrade_required";

export type PartyChatActivityKind = "user" | "winner" | "podium";

export interface PartyChatActivity {
  at: string;
  kind: PartyChatActivityKind;
  userId: string | null;
}

export interface PartyChatPreviewUser {
  kind: "user";
  id: string;
  at: string;
  userId: string;
  senderName: string;
  body: string;
}

export interface PartyChatPreviewWinner {
  kind: "winner";
  id: string;
  at: string;
  categoryName: string;
  winnerName: string;
}

export interface PartyChatPreviewPodium {
  kind: "podium";
  id: string;
  at: string;
  climberName: string;
  passedName: string;
  toPlace: PodiumPlace;
  variant: PodiumPassVariant;
  style?: PodiumPassStyle;
  otherClimbers?: PodiumOtherClimbers;
}

export type PartyChatPreviewItem =
  | PartyChatPreviewUser
  | PartyChatPreviewWinner
  | PartyChatPreviewPodium;

export interface PartyChatListCue {
  latestActivity: PartyChatActivity | null;
  latestPreview: PartyChatPreviewItem | null;
}

/** GET /api/parties/[partyId]/chat — response body */
export interface GetPartyChatResponse {
  access: PartyChatAccess;
  messages: PartyChatMessage[];
  currentUserId: string;
  error: string | null;
}

/** GET /api/parties/[partyId]/chat/summary — response body */
export interface GetPartyChatSummaryResponse {
  access: PartyChatAccess;
  latestActivity: PartyChatActivity | null;
  recent: PartyChatPreviewItem[];
  currentUserId: string;
  error: string | null;
}

/** POST /api/parties/[partyId]/chat — request body */
export interface SendPartyChatMessageBody {
  body: string;
}

/** POST /api/parties/[partyId]/chat — success response body */
export interface SendPartyChatMessageResponse {
  message: PartyChatUserMessage;
}
